import { publish, subscribe, PUB_SUB_EVENTS } from '@/scripts/core/global'

/**
 * Collection Filters (ECOMM-3880)
 *
 * A chip row that filters the product grid in place by product-level tag, e.g.
 * "Category: Bundles". Single-select, per the brief: choosing a second chip
 * replaces the first, and choosing the active chip again clears the filter.
 *
 * Items are only hidden, never reordered or re-rendered, so the existing grid
 * order is preserved and non-matching products are simply removed. Each grid
 * item carries its matching tags in data-uc-product-tags, pipe separated.
 * A chip's data-filter-tag may hold several tags, comma separated; the chip
 * matches an item when the item carries ANY of them.
 */

const ROOT_SELECTOR = '[data-collection-filters]'
const CHIP_SELECTOR = '[data-filter-chip]'
const ITEM_SELECTOR = '[data-uc-product-tags]'
const ACTIVE_CLASS = 'is-active'
const FILTERED_OUT_CLASS = 'is-filtered-out'

/** Query parameter carrying the applied chip tag, so the view is shareable. */
const URL_PARAM = 'filter_tag'

function tagsOf(item: Element): string[] {
	return (item.getAttribute('data-uc-product-tags') || '')
		.split('|')
		.map((tag) => tag.trim())
		.filter(Boolean)
}

/**
 * The tag value a chip puts in the URL.
 *
 * The raw tag is preserved: Shopify's Search & Discovery matches filter.p.tag
 * against the tag verbatim, so the previous slug form (lowercased, every
 * non-alphanumeric turned into a hyphen) meant 'a_upsell_pvolve-slant-board'
 * was written as 'a-upsell-pvolve-slant-board' and matched no product at all.
 * Only the optional "Category:" convention prefix is stripped.
 */
function urlTag(tag: string): string {
	return tag.replace(/^category:\s*/i, '').trim()
}

function activeTagOf(root: HTMLElement): string | null {
	return root.dataset.activeFilterTag || null
}

/** Split a chip's tag setting, which may list several, into individual tags. */
function parseChipTags(value: string): string[] {
	return value
		.split(',')
		.map((t) => t.trim())
		.filter(Boolean)
}

/** Slug for a chip's whole tag list, e.g. "Category: Bundles, weights" -> "bundles,weights". */
function urlTagList(value: string): string {
	return parseChipTags(value).map(urlTag).filter(Boolean).join(',')
}

const PAGINATION_SELECTOR = 'infinite-scroll'

// Held while a load-through is expected or in flight. With a filter active the grid
// is still filling, so a count written now is a running total (0 -> 6 -> 7) rather
// than the filtered one. The final value is written once when the run reports end.
let countHeld = false

// The last product-count text this module settled on. While a run is in flight the
// server's own render (which knows nothing about the filter) would otherwise put the
// unfiltered total back on screen, so a held count re-asserts this instead.
let settledCountText: string | null = null

/** Apply the root's current filter: mark the chips and hide non-matching items. */
function applyFilter(root: HTMLElement, notify = false): void {
	const activeTag = activeTagOf(root)

	// A user action is what starts a load-through, so hold from before the first
	// write. Only hold when pagination exists: with nothing to load, no run would
	// report back and the count would never settle. Clearing the filter releases it.
	if (notify) {
		countHeld =
			activeTag !== null &&
			document.querySelector(PAGINATION_SELECTOR) !== null
	}
	if (activeTag === null) countHeld = false

	root.querySelectorAll<HTMLElement>(CHIP_SELECTOR).forEach((chip) => {
		const isActive =
			activeTag !== null && chip.dataset.filterTag === activeTag
		chip.classList.toggle(ACTIVE_CLASS, isActive)
		chip.setAttribute('aria-pressed', String(isActive))
	})

	let matched = 0
	document.querySelectorAll<HTMLElement>(ITEM_SELECTOR).forEach((item) => {
		const wanted = activeTag === null ? [] : parseChipTags(activeTag)
		const itemTags = tagsOf(item)
		const matches =
			activeTag === null || wanted.some((t) => itemTags.indexOf(t) !== -1)
		if (matches) matched += 1
		item.classList.toggle(FILTERED_OUT_CLASS, !matches)
	})
	if (countHeld) {
		writeSettledCount()
	} else {
		updateProductCount(root, matched)
	}

	// Tell pagination to stop auto-loading while a filter is applied: filtering
	// removes items, the Load More button scrolls into view, and its observer
	// would otherwise keep fetching pages the filter hides.
	//
	// Only a user action announces the change. Every appended page re-runs
	// applyFilter through the MutationObserver, so announcing from there would
	// feed loadThrough back into itself: append -> applyFilter -> publish ->
	// loadThrough -> append, without end.
	if (notify) publish(PUB_SUB_EVENTS.collectionFilterChange, { activeTag })
}

/**
 * Apply the root's stored filter to the current grid and keep the URL in step.
 * applyFilter publishes collectionFilterChange, which is what tells pagination
 * to suspend or re-run its loading, so both callers get that for free.
 */
function commitFilter(root: HTMLElement): void {
	applyFilter(root, true)
	writeUrl(root)
}

/**
 * Keep the URL in step with the applied filter so the view can be shared.
 * replaceState rather than pushState: filtering is a view tweak, not a
 * navigation, and it must not fill the back stack.
 */
function writeUrl(root: HTMLElement): void {
	const activeTag = activeTagOf(root)
	const url = new URL(window.location.href)
	if (activeTag) {
		url.searchParams.set(URL_PARAM, urlTagList(activeTag))
	} else {
		url.searchParams.delete(URL_PARAM)
	}
	window.history.replaceState({}, '', url.toString())
}

/**
 * Apply a filter carried in the URL. Only tags this page actually offers as a
 * chip are honoured, so a link from another collection cannot hide everything.
 */
function restoreFromUrl(root: HTMLElement): void {
	const raw = new URL(window.location.href).searchParams.get(URL_PARAM)
	if (!raw) return
	const wanted = urlTagList(raw)
	if (!wanted) return
	// Only tags this page offers as a chip are honoured, so a link from another
	// collection cannot hide everything.
	const chip = Array.from(
		root.querySelectorAll<HTMLElement>(CHIP_SELECTOR)
	).find((c) => urlTagList(c.dataset.filterTag || '') === wanted)
	if (!chip) return
	root.dataset.activeFilterTag = chip.dataset.filterTag || ''
	applyFilter(root, true)
}

/**
 * Keep the theme's product count in step with the filter. The server renders it
 * (and re-renders it on facet changes), but filtering only hides DOM nodes, so
 * without this the count keeps reporting the whole collection while a chip is on.
 * The original text is cached on first touch so clearing the filter restores it.
 */
const COUNT_TARGETS = ['#ProductCount', '#ProductCountDesktop']

function countTargets(): HTMLElement[] {
	return COUNT_TARGETS.map((selector) =>
		document.querySelector<HTMLElement>(selector)
	).filter((element): element is HTMLElement => element !== null)
}

function countTextFor(root: HTMLElement, matched: number): string | null {
	const template =
		(matched === 1 ? root.dataset.countOne : root.dataset.countOther) || ''
	return template ? template.replace('[count]', String(matched)) : null
}

/** Write the count for the items currently matching, and remember the text. */
function updateProductCount(root: HTMLElement, matched: number): void {
	const filtered = activeTagOf(root) !== null
	const filteredText = countTextFor(root, matched)
	countTargets().forEach((element) => {
		if (element.dataset.filterCountOriginal === undefined) {
			element.dataset.filterCountOriginal = element.textContent || ''
		}
		const original = element.dataset.filterCountOriginal
		if (!filtered) {
			element.textContent = original
			settledCountText = original
			return
		}
		if (filteredText !== null) {
			element.textContent = filteredText
			settledCountText = filteredText
		}
	})
}

/**
 * Re-assert the last settled count while a run is in flight, so the server's render
 * cannot show the unfiltered total mid-load. Runs in the same task as that render, so
 * the total is never painted.
 */
function writeSettledCount(): void {
	if (settledCountText === null) return
	countTargets().forEach((element) => {
		element.textContent = settledCountText
	})
}

function bindRoot(root: HTMLElement): void {
	// Sections are re-rendered in the theme editor; don't stack listeners.
	if (root.dataset.collectionFiltersBound === 'true') return
	root.dataset.collectionFiltersBound = 'true'

	// The CTA Clicked payload reads cta_target_url from the element. A chip is a
	// button with no href, so resolve where it points from the binding time — not
	// at click time, where the theme's listener may already have fired.
	root.querySelectorAll<HTMLElement>(CHIP_SELECTOR).forEach((chip) => {
		const chipTags = chip.dataset.filterTag || ''
		if (!chipTags) return
		const url = new URL(window.location.href)
		url.searchParams.set(URL_PARAM, urlTagList(chipTags))
		chip.dataset.ctaTargetUrl = url.toString()
	})

	root.addEventListener('click', (event) => {
		const target = event.target as HTMLElement | null
		const chip = target?.closest<HTMLElement>(CHIP_SELECTOR)
		if (!chip || !root.contains(chip)) return

		const tag = chip.dataset.filterTag || ''
		if (!tag) return

		// Clicking the active chip clears the filter, otherwise it replaces it.
		// Chip clicks are reported by the theme's standard CTA Clicked listener,
		// which binds to this button through data-cta-name (snippets/segment.liquid).
		root.dataset.activeFilterTag = activeTagOf(root) === tag ? '' : tag
		commitFilter(root)
	})
}

/**
 * The grid's "Load More" appends another page of products into the same list,
 * so newly added items have to honour the filter that is already applied.
 */
function observeList(): void {
	const list = document.querySelector(ITEM_SELECTOR)?.parentElement
	if (!list) return

	const observer = new MutationObserver((mutations) => {
		if (!mutations.some((mutation) => mutation.addedNodes.length > 0))
			return
		document
			.querySelectorAll<HTMLElement>(ROOT_SELECTOR)
			// Deliberately not notifying: this fires on every appended page, and
			// announcing from here is what fed loadThrough back into itself.
			// forEach would also pass the index as the notify argument.
			.forEach((root) => applyFilter(root))
	})
	observer.observe(list, { childList: true })
}

export function collectionFilters(): void {
	const roots = Array.from(
		document.querySelectorAll<HTMLElement>(ROOT_SELECTOR)
	)
	if (!roots.length) return

	roots.forEach(bindRoot)
	roots.forEach(restoreFromUrl)
	observeList()

	// Sorting and the other facet controls re-render the grid section, which
	// replaces the items and drops their hidden state while the chip stays
	// active. Re-apply so the chip and the grid keep agreeing.
	// The run has settled, so the grid now holds every matching item: release the hold
	// and write the count once. Re-applying silently (notify is false) recomputes it
	// without starting another run.
	subscribe(PUB_SUB_EVENTS.loadThroughEnd, () => {
		if (!countHeld) return
		countHeld = false
		document
			.querySelectorAll<HTMLElement>(ROOT_SELECTOR)
			.forEach((root) => {
				applyFilter(root)
			})
	})

	subscribe(PUB_SUB_EVENTS.gridRender, () => {
		// The grid's innerHTML was replaced, so the old list node (and the
		// observer watching it) is detached. Re-observe the new one, otherwise
		// pages appended by the load-through would arrive unfiltered.
		observeList()
		document
			.querySelectorAll<HTMLElement>(ROOT_SELECTOR)
			.forEach((root) => {
				if (!activeTagOf(root)) return
				commitFilter(root)
			})
	})

	// Restore/augment state after (re)rendering in the theme editor.
	document.addEventListener('shopify:section:load', () => {
		document
			.querySelectorAll<HTMLElement>(ROOT_SELECTOR)
			.forEach((root) => {
				bindRoot(root)
				applyFilter(root, true)
			})
	})
}
